import { useEffect, useState } from "react";
import { FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useGet } from "@/hooks/useResource";
import { Loading, PageHeader, StatusBadge } from "@/components/common";
import { MenuSelection } from "@/components/menu/MenuSelection";
import { EMPTY_MENU, menuFormFromDoc, menuPayload } from "@/lib/menuModule";
import { Button } from "@/components/ui/button";

export default function MenuPage() {
  const { data, loading, refetch } = useGet("/client/menu");
  const { data: catalogue } = useGet("/menu-items");
  const [form, setForm] = useState(EMPTY_MENU);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (data) {
      setForm(menuFormFromDoc(data));
    }
  }, [data]);

  const locked = data?.client_status === "confirmed";

  const save = async (clientStatus) => {
    setSaving(clientStatus);
    try {
      await api.put("/client/menu", { ...menuPayload(form), client_status: clientStatus });
      toast.success(clientStatus === "confirmed" ? "Menu confirmed." : "Menu draft saved.");
      refetch();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return <Loading label="Loading menu" />;
  }

  return (
    <div data-testid="menu-page">
      <PageHeader
        eyebrow="Food & Beverage"
        title="Menu"
        description="Our seasonal wedding menu. Choose up to two dishes per course, save a draft while you decide, then confirm."
      >
        <StatusBadge
          status={data?.client_status}
          at={data?.confirmed_at}
          by={data?.confirmed_by}
          testid="menu-status-badge"
        />
        {!locked && (
          <>
            <Button
              onClick={() => save("draft")}
              disabled={!!saving}
              className="rounded-full bg-primary hover:bg-primary/90"
              data-testid="detail-save-btn"
            >
              <FloppyDisk size={18} className="mr-1" />
              {saving === "draft" ? "Saving…" : "Save draft"}
            </Button>
            <Button
              onClick={() => save("confirmed")}
              disabled={!!saving}
              variant="outline"
              className="rounded-full"
              data-testid="detail-confirm-btn"
            >
              {saving === "confirmed" ? "Confirming…" : "Confirm"}
            </Button>
          </>
        )}
      </PageHeader>
      {locked && (
        <p className="mb-6 text-sm text-muted-foreground" data-testid="menu-locked-note">
          Your menu is confirmed. Ask the Events Team if a correction is needed.
        </p>
      )}
      <MenuSelection catalogue={catalogue} form={form} setForm={setForm} locked={locked} />
    </div>
  );
}
